import { useState } from 'react'
import { marked } from '../utils/marked'
import type { AgentPrediction } from '../types'
import { useForecastStore } from '../store/forecastStore'
import { linkifyPlainUrls, stripUnhelpfulCitations, urlHostname } from '../utils/text'

interface AgentCardProps {
  agentId: number
  agentName?: string
  prediction?: AgentPrediction
  useLiveState?: boolean
}

export default function AgentCard({ agentId, agentName, prediction, useLiveState = true }: AgentCardProps) {
  const [expanded, setExpanded] = useState(false)
  const [showThoughtProcess, setShowThoughtProcess] = useState(false)
  const [showAllSources, setShowAllSources] = useState(false)
  const [showModal, setShowModal] = useState(false)
  // Always call hooks unconditionally (rules of hooks).
  const storeAgentState = useForecastStore((state) => state.activeAgentStates[agentId])
  const agentState = useLiveState ? storeAgentState : undefined

  const displayName = agentName || `Agent ${String(agentId).padStart(2, '0')}`
  // Prefer a completed prediction over any stale pending state (can happen if state initialises before prediction arrives).
  const status =
    prediction
      ? agentState?.status === 'running'
        ? 'running'
        : 'complete'
      : agentState?.status || 'pending'
  const reasoning = agentState?.reasoning || prediction?.reasoning_trace || ''
  const searches = (agentState?.searches || (prediction?.searches as any) || []) as Array<{ query: string; results: any[] }>
  const visitedDomains =
    (agentState?.visited_domains ||
      (() => {
        const domains = new Set<string>()
        for (const s of searches || []) {
          for (const r of s.results || []) {
            if (r?.url) domains.add(urlHostname(r.url))
          }
        }
        for (const u of (prediction?.visited_urls as any) || []) {
          const url = typeof u === 'string' ? u : u?.url
          if (url) domains.add(urlHostname(url))
        }
        return Array.from(domains)
      })()) as string[]

  const sourcesByDomain = (() => {
    const map = new Map<string, string>()
    for (const s of searches || []) {
      for (const r of s.results || []) {
        if (!r?.url) continue
        const d = urlHostname(r.url)
        if (!map.has(d)) map.set(d, r.url)
      }
    }
    return map
  })()

  const probPct =
    prediction && typeof prediction.probability === 'number'
      ? Math.round(prediction.probability * 100)
      : null

  const getStatusText = () => {
    if (status === 'complete' && probPct != null) {
      return `${probPct}%`
    }
    if (status === 'running') {
      const searchCount = searches.length
      if (searchCount > 0) {
        const lastSearch = searches[searches.length - 1]
        return `Reading ${lastSearch.results?.length || 0} sources...`
      }
      return 'Starting...'
    }
    return 'Waiting...'
  }

  const getProgress = () => {
    if (status === 'complete') return 100
    if (status === 'running') {
      return Math.min(searches.length * 20, 80)
    }
    return 0
  }

  const getConfidenceColor = () => {
    if (!prediction?.confidence) return '#4a6b7c' // Steel Blue (neutral)
    const conf = prediction.confidence.toLowerCase()
    if (conf === 'high') return '#2f6b3d' // Green
    if (conf === 'medium') return '#b8924a' // Gold
    if (conf === 'low') return '#a84848' // Clay Red
    return '#4a6b7c' // Steel Blue (default)
  }

  return (
    <section className="rounded-delphi border border-rule bg-paper2 shadow-delphi overflow-hidden">
      <button
        type="button"
        className="w-full px-3 py-2.5 flex items-center justify-between gap-3 bg-paper/40 hover:bg-white/30 transition"
        onClick={() => {
          if (!useLiveState && status === 'complete' && prediction) {
            setShowModal(true)
          } else {
            setExpanded(!expanded)
          }
        }}
        aria-expanded={useLiveState ? expanded : showModal}
      >
        <span className="text-xs text-muted truncate">{displayName}</span>
        <span className="flex items-center gap-3">
          {status === 'complete' && probPct != null && (
            <span className="font-serif text-2xl text-ink leading-none">{probPct}%</span>
          )}
          <span className="text-xs text-muted">{expanded ? '▾' : '▸'}</span>
        </span>
      </button>

      <div className="px-3 pb-3 pt-2">
        <div className="flex items-baseline justify-between gap-3">
          <div className="text-sm text-ink">{getStatusText()}</div>
          {status === 'complete' && prediction?.confidence && (
            <div 
              className="text-[10px] font-semibold tracking-wider px-2 py-0.5 rounded"
              style={{ 
                color: getConfidenceColor(),
                backgroundColor: `${getConfidenceColor()}15`
              }}
            >
              {prediction.confidence.toString().toUpperCase()} CONFIDENCE
            </div>
          )}
        </div>

        <div className="mt-2 h-2 rounded-full border border-rule bg-paper overflow-hidden">
          <div
            className="h-full transition-all duration-300"
            style={{ 
              width: `${getProgress()}%`,
              backgroundColor: status === 'complete' ? getConfidenceColor() : 'rgba(47, 107, 61, 0.3)',
              opacity: status === 'complete' ? 0.4 : 0.3
            }}
          />
        </div>
      </div>

      {expanded && (
        <div className="px-3 pb-4">
          {status === 'complete' && prediction && (
            <div className="rounded-delphi border border-rule bg-paper p-3">
              <div className="font-semibold text-ink">Analysis</div>
              <div
                className="mt-2 text-sm text-ink/90 leading-relaxed [&>p]:mt-3 [&>p:first-child]:mt-0 [&>ul]:mt-3 [&>ol]:mt-3 [&>h3]:mt-4"
                dangerouslySetInnerHTML={{
                  __html: marked.parse(
                    [
                      prediction.question_restatement && `**Question:** ${prediction.question_restatement}\n\n`,
                      prediction.reasoning && `### Analysis\n\n${linkifyPlainUrls(stripUnhelpfulCitations(prediction.reasoning))}\n\n`,
                      prediction.key_evidence &&
                        prediction.key_evidence.length > 0 &&
                        `### Key Evidence\n\n${prediction.key_evidence.map((e, i) => `${i + 1}. ${linkifyPlainUrls(stripUnhelpfulCitations(e))}`).join('\n')}\n\n`,
                      prediction.notes && `**Notes:** ${linkifyPlainUrls(stripUnhelpfulCitations(prediction.notes))}`,
                    ]
                      .filter(Boolean)
                      .join('')
                  ),
                }}
              />
            </div>
          )}

          {searches.length > 0 && (
            <div className="mt-3 rounded-delphi border border-rule bg-paper p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="font-semibold text-ink">Searches</div>
                <div className="text-[11px] text-muted">({searches.length})</div>
              </div>
              <div className="mt-2 space-y-2">
                {searches.map((search, idx) => (
                  <div key={idx} className="text-xs text-muted">
                    <div className="truncate">Searching: {search.query}</div>
                    <div className="mt-1 space-y-1">
                      {(showAllSources ? search.results : search.results?.slice(0, 3))?.map((result: any, rIdx: number) => (
                        <div key={rIdx} className="truncate">
                          <a className="text-ink hover:underline" href={result.url} target="_blank" rel="noopener noreferrer">
                            {(!result.title || result.title === 'Unknown') && result.url ? urlHostname(result.url) : (result.title || result.url)}
                          </a>
                        </div>
                      ))}
                      {search.results && search.results.length > 3 && !showAllSources && (
                        <button
                          type="button"
                          className="text-muted hover:text-ink transition underline-offset-2 hover:underline"
                          onClick={() => setShowAllSources(true)}
                        >
                          +{search.results.length - 3} more
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {showAllSources && (
                <button
                  type="button"
                  className="mt-2 text-[11px] text-muted hover:text-ink transition underline-offset-2 hover:underline"
                  onClick={() => setShowAllSources(false)}
                >
                  Show fewer sources
                </button>
              )}
            </div>
          )}

          {visitedDomains.length > 0 && (
            <div className="mt-3 rounded-delphi border border-rule bg-paper p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="font-semibold text-ink">Sources visited</div>
                <div className="text-[11px] text-muted">({visitedDomains.length})</div>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {visitedDomains.slice(0, 16).map((d) => (
                  <a
                    key={d}
                    className="inline-flex items-center rounded-full border border-rule bg-paper2 px-2 py-0.5 text-[11px] text-muted"
                    title={d}
                    href={sourcesByDomain.get(d) || `https://${d}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {d}
                  </a>
                ))}
                {visitedDomains.length > 16 && (
                  <span className="text-[11px] text-muted">+{visitedDomains.length - 16} more</span>
                )}
              </div>
            </div>
          )}

          {reasoning && (
            <div className="mt-3">
              <button
                type="button"
                className="inline-flex items-center gap-2 text-xs text-muted hover:text-ink transition"
                onClick={() => setShowThoughtProcess(!showThoughtProcess)}
              >
                <span className="text-[10px]">{showThoughtProcess ? '▾' : '▸'}</span>
                <span>{showThoughtProcess ? 'Hide' : 'Show'} thought process</span>
              </button>
              {showThoughtProcess && (
                <div className="mt-2 rounded-delphi border border-rule bg-paper p-3 text-xs font-mono text-muted whitespace-pre-wrap">
                  {stripUnhelpfulCitations(reasoning)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {showModal && prediction && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(ev) => ev.stopPropagation()}>
            <div className="modal-header">
              <h2 className="text-lg sm:text-xl font-semibold text-ink">{displayName}</h2>
              <button className="modal-close" type="button" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="rounded-delphi border border-rule bg-paper p-3">
                <div className="font-semibold text-ink">Analysis</div>
                <div
                  className="mt-2 text-sm text-ink/90 leading-relaxed [&>p]:mt-3 [&>p:first-child]:mt-0 [&>ul]:mt-3 [&>ol]:mt-3 [&>h3]:mt-4"
                  dangerouslySetInnerHTML={{
                    __html: marked.parse(
                      [
                        prediction.question_restatement && `**Question:** ${prediction.question_restatement}\n\n`,
                        prediction.reasoning && `### Analysis\n\n${linkifyPlainUrls(stripUnhelpfulCitations(prediction.reasoning))}\n\n`,
                        prediction.key_evidence &&
                          prediction.key_evidence.length > 0 &&
                          `### Key Evidence\n\n${prediction.key_evidence.map((e, i) => `${i + 1}. ${linkifyPlainUrls(stripUnhelpfulCitations(e))}`).join('\n')}\n\n`,
                        prediction.notes && `**Notes:** ${linkifyPlainUrls(stripUnhelpfulCitations(prediction.notes))}`,
                      ]
                        .filter(Boolean)
                        .join('')
                    ),
                  }}
                />
              </div>

              {searches.length > 0 && (
                <div className="mt-4 rounded-delphi border border-rule bg-paper p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-semibold text-ink">Searches</div>
                    <div className="text-[11px] text-muted">({searches.length})</div>
                  </div>
                  <div className="mt-2 space-y-2">
                    {searches.map((search, idx) => (
                      <div key={idx} className="text-xs text-muted">
                        <div className="truncate">Searching: {search.query}</div>
                        <div className="mt-1 space-y-1">
                          {(search.results || []).map((result: any, rIdx: number) => (
                            <div key={rIdx} className="truncate">
                              <a className="text-ink hover:underline" href={result.url} target="_blank" rel="noopener noreferrer">
                                {(!result.title || result.title === 'Unknown') && result.url ? urlHostname(result.url) : (result.title || result.url)}
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {visitedDomains.length > 0 && (
                <div className="mt-4 rounded-delphi border border-rule bg-paper p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-semibold text-ink">Sources visited</div>
                    <div className="text-[11px] text-muted">({visitedDomains.length})</div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {visitedDomains.map((d) => (
                      <a
                        key={d}
                        className="inline-flex items-center rounded-full border border-rule bg-paper2 px-2 py-0.5 text-[11px] text-muted"
                        title={d}
                        href={sourcesByDomain.get(d) || `https://${d}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {d}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {reasoning && (
                <div className="mt-4">
                  <div className="font-semibold text-ink">Thought process</div>
                  <div className="mt-2 rounded-delphi border border-rule bg-paper p-3 text-xs font-mono text-muted whitespace-pre-wrap">
                    {stripUnhelpfulCitations(reasoning)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

